import axios from "axios";
import { arrType, datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";

export const PO_API = "/api/purchase-order";
export const poUrlKey = "po_id";

export const masterEndpoints = {
	sites: `${PO_API}/masters/sites`,
	vendors: `${PO_API}/masters/vendors`,
	currencies: `${PO_API}/masters/currencies`,
	tradeGroups: `${PO_API}/masters/trade-groups`,
	agents: `${PO_API}/masters/agents`,
	transporters: `${PO_API}/masters/transporters`,
	merchandisers: `${PO_API}/masters/merchandisers`,
	channels: `${PO_API}/masters/channels`,
	items: `${PO_API}/masters/items`,
};

const boolOptions = [
	{ name: "Yes", key: true },
	{ name: "No", key: false },
];

const purchaseTypeOptions = [
	{ name: "Non-Consignment", key: false },
	{ name: "Consignment", key: true },
];

const entryModeOptions = [
	{ name: "Items", key: "I" },
	{ name: "Sets", key: "S" },
];

const scalarText = (value) => {
	if (value == null) return "";
	if (typeof value === "object" && value.value != null) return scalarText(value.value);
	return String(value).trim();
};

/** Normalize BQ master rows for SearchInputFieldWithDebounce ({ key, name }). Idempotent. */
export const mapMasterRow = (row) => {
	const code = row?.code ?? row?.key ?? row?.item_code;
	const shortName = scalarText(row?.short_name ?? row?.shrtname ?? row?.SHRTNAME ?? row?.abbre ?? row?.ABBRE);
	const rawName = scalarText(row?.name ?? row?.item_name);
	const prefix = code != null && code !== "" ? `${code} — ` : "";
	const alreadyLabeled = rawName && prefix && rawName.startsWith(prefix);
	const displayName = alreadyLabeled ? rawName : rawName ? `${prefix}${rawName}` : String(code ?? "");
	return {
		...row,
		key: code,
		name: displayName,
		// Keep for shared sites cache (SO remarks = owner short name)
		...(shortName ? { short_name: shortName } : {}),
	};
};

/** Channel UDF value is both the code and the label — do not prefix "code — name". */
export const mapChannelMasterRow = (row) => {
	const label = scalarText(row?.name ?? row?.code ?? row?.key);
	return {
		...row,
		key: label,
		code: label,
		name: label,
	};
};

/** Dropdown label: Name (Short Name) - Code. */
const nameShortCodeLabel = (name, shortName, code) => {
	const codeText = code != null && code !== "" ? String(code) : "";
	const fullName = scalarText(name);
	const short = scalarText(shortName);
	const left =
		fullName && short && short.toUpperCase() !== fullName.toUpperCase()
			? `${fullName} (${short})`
			: fullName || short;
	return left && codeText ? `${left} - ${codeText}` : left || codeText;
};

/** Vendor label = SLNAME (ABBRE) - SLCODE. Stored value = SLCODE. */
export const mapVendorMasterRow = (row) => {
	const code = row?.code ?? row?.key;
	const codeText = code != null && code !== "" ? String(code) : "";
	const slName = scalarText(row?.slname ?? row?.SLNAME) || scalarText(row?.name);
	const abbre = scalarText(row?.abbre ?? row?.ABBRE ?? row?.short_name);
	const left = slName && abbre ? `${slName} (${abbre})` : slName || abbre;
	const label = left && codeText ? `${left} - ${codeText}` : left || codeText;
	return {
		...row,
		key: code,
		slname: slName,
		abbre,
		short_name: abbre,
		name: label,
	};
};
export const mapShortNameMasterRow = (row) => {
	const code = row?.code ?? row?.key;
	const shortName = scalarText(row?.short_name ?? row?.shrtname ?? row?.SHRTNAME);
	const rawName = scalarText(row?.site_name ?? row?.NAME) || scalarText(row?.name);
	const label = nameShortCodeLabel(rawName, shortName, code);
	return {
		...row,
		key: code,
		name: label,
		short_name: shortName || rawName,
	};
};

export const parseItemRate = (value) => {
	if (value == null || value === "") return null;
	if (typeof value === "object" && value.value != null) return parseItemRate(value.value);
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
};

/** Strip barcode/code prefix from dropdown label → plain INVITEM name. */
export const itemDescriptionFromMaster = (row) => {
	const rawName = scalarText(row?.item_description ?? row?.item_name ?? row?.name);
	if (!rawName) return "";
	const itemCode = scalarText(row?.item_code ?? row?.code);
	const barcode = scalarText(row?.barcode);
	const idPart =
		itemCode && barcode && barcode !== itemCode
			? `${barcode} / ${itemCode}`
			: barcode || itemCode;
	if (idPart && rawName.startsWith(`${idPart} — `)) {
		return rawName.slice(idPart.length + 3).trim();
	}
	if (idPart && rawName.startsWith(`${idPart} - `)) {
		return rawName.slice(idPart.length + 3).trim();
	}
	return rawName;
};

/** Item master: dropdown key = barcode (user selects barcode; ICODE kept as item_code). */
export const mapItemMasterRow = (row) => {
	const itemCode = scalarText(row?.item_code ?? row?.code ?? row?.key);
	const barcodeRaw = scalarText(row?.barcode);
	const barcode = barcodeRaw || itemCode;
	// Ginesys "Item Description" ≈ article name (INVARTICLE.NAME), not ITEM_NAME.
	const itemDescription = scalarText(
		row?.item_description ?? row?.article_name ?? row?.ARTICLE_NAME ?? row?.item_name ?? row?.name
	);
	const idPart =
		itemCode && barcode !== itemCode ? `${barcode} / ${itemCode}` : barcode || itemCode;
	const prefix = idPart ? `${idPart} — ` : "";
	const alreadyLabeled = itemDescription && prefix && itemDescription.startsWith(prefix);
	const plainDescription = alreadyLabeled
		? itemDescriptionFromMaster({ ...row, name: itemDescription })
		: itemDescription;
	const displayName = plainDescription ? `${prefix}${plainDescription}` : idPart;
	// PO unit price = INVITEM.COSTRATE (purchase); RATE is often selling/list and can be 0.
	const cost = parseItemRate(row?.cost ?? row?.COSTRATE ?? row?.COST);
	const listRate = parseItemRate(row?.rate ?? row?.RATE);
	const rate = cost != null ? cost : listRate;
	// Unique option key so duplicate barcodes / blank barcodes don't collapse the list
	const key = barcode && itemCode && barcode !== itemCode ? `${barcode}__${itemCode}` : barcode || itemCode;
	return {
		key,
		barcode,
		item_code: itemCode,
		code: itemCode,
		article_code: row?.article_code ?? row?.INVARTICLE_CODE ?? null,
		article_name: scalarText(row?.article_name ?? row?.ARTICLE_NAME),
		item_description: plainDescription,
		cost,
		list_rate: listRate,
		rate,
		name: displayName,
	};
};

/** When barcode is a search option object, flatten barcode + item_code + rate from INVITEM. */
export const syncPoItemDraft = (draft) => {
	const next = { ...draft };
	const selected = draft?.barcode;
	if (selected && typeof selected === "object") {
		next.barcode = selected.barcode ?? selected.key ?? "";
		next.item_code = selected.item_code ?? selected.code ?? "";
		next.item_description =
			selected.item_description || itemDescriptionFromMaster(selected) || "";
		const rate = parseItemRate(selected.rate);
		next.rate = rate;
	} else if (selected == null || selected === "") {
		next.item_code = "";
		next.item_description = "";
		next.rate = null;
	}
	return next;
};

export const poPartyCode = (value) => {
	if (value == null || value === "") return null;
	if (typeof value === "object") {
		const code = value.key ?? value.code ?? null;
		return code == null || code === "" ? null : code;
	}
	return value;
};

export const createPoMasterFetch = (endpoint, { minSearch = 0, limit: defaultLimit = 20, mapRow = mapMasterRow, extraParams } = {}) => {
	return async (offset = 0, limit = defaultLimit, searchTerm = "") => {
		const q = (searchTerm || "").trim();
		if (minSearch > 0 && q.length < minSearch) {
			return { data: { result: [] } };
		}
		const safeOffset = Number.isFinite(Number(offset)) && Number(offset) >= 0 ? Number(offset) : 0;
		const safeLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : defaultLimit;
		const extra = typeof extraParams === "function" ? extraParams() : extraParams || {};
		const res = await axios.get(endpoint, {
			params: { q, search: q, offset: safeOffset, limit: safeLimit, ...extra },
		});
		return {
			data: {
				result: (res.data?.result || []).map(mapRow),
			},
		};
	};
};

/** Vendors assigned to the owner site's organization (FINSLOP.ADMOU_CODE = ADMSITE.ADMOU_CODE). */
export const fetchPoVendorsForSite = (ownerSiteCode) => {
	return async (offset, limit, searchTerm) => {
		if (ownerSiteCode == null || ownerSiteCode === "") {
			return { data: { result: [] } };
		}
		return createPoMasterFetch(masterEndpoints.vendors, {
			mapRow: mapVendorMasterRow,
			extraParams: { owner_site_code: ownerSiteCode },
		})(offset, limit, searchTerm);
	};
};

export const poMasterFetches = {
	owner_site_code: createPoMasterFetch(masterEndpoints.sites, { mapRow: mapShortNameMasterRow }),
	currency_code: createPoMasterFetch(masterEndpoints.currencies),
	trade_group_code: createPoMasterFetch(masterEndpoints.tradeGroups),
	agent_code: createPoMasterFetch(masterEndpoints.agents),
	transporter_code: createPoMasterFetch(masterEndpoints.transporters),
	merchandiser_code: createPoMasterFetch(masterEndpoints.merchandisers),
	channel: createPoMasterFetch(masterEndpoints.channels, { mapRow: mapChannelMasterRow }),
	barcode: createPoMasterFetch(masterEndpoints.items, { minSearch: 2, mapRow: mapItemMasterRow }),
	item_code: createPoMasterFetch(masterEndpoints.items, { minSearch: 2, mapRow: mapItemMasterRow }),
};

const masterMatch = (rows, code) =>
	(rows || []).find((row) => String(row?.key ?? row?.code ?? "") === String(code)) || null;

const lookupMasterLabel = async (fetchFn, code) => {
	if (code == null || code === "" || typeof fetchFn !== "function") return "";
	try {
		const res = await fetchFn(0, 20, String(code));
		return masterMatch(res?.data?.result, code)?.name || "";
	} catch {
		return "";
	}
};

/** Names for codes already saved on a purchase order. Does not change what is stored. */
export const resolvePoHeaderLabels = async (form) => {
	const ownerSite = poPartyCode(form?.owner_site_code);
	const lookups = [
		["owner_site_code", poMasterFetches.owner_site_code, ownerSite],
		["vendor_code", ownerSite != null ? fetchPoVendorsForSite(ownerSite) : null, poPartyCode(form?.vendor_code)],
		["currency_code", poMasterFetches.currency_code, poPartyCode(form?.currency_code)],
		["agent_code", poMasterFetches.agent_code, poPartyCode(form?.agent_code)],
		["transporter_code", poMasterFetches.transporter_code, poPartyCode(form?.transporter_code)],
		["merchandiser_code", poMasterFetches.merchandiser_code, poPartyCode(form?.merchandiser_code)],
		["channel", poMasterFetches.channel, form?.channel],
		["trade_group_name", poMasterFetches.trade_group_code, poPartyCode(form?.trade_group_code)],
	];
	const entries = await Promise.all(
		lookups.map(async ([key, fetchFn, code]) => [key, await lookupMasterLabel(fetchFn, code)])
	);
	return Object.fromEntries(entries.filter(([, name]) => name));
};

const bqSearchColumn = (name, key, { required = false } = {}) => ({
	name,
	key,
	input_type: inputTypes.SELECT.key,
	data_type: datatypes.STRING.key,
	editable: true,
	constraints: { NOTNULL: required, DISPLAY: true },
	use_bq_search: true,
	[selectOptionsKeyName]: {
		type: arrType,
		list: [],
	},
});

const enterColumn = (name, key, dataType = datatypes.STRING.key, { required = false } = {}) => ({
	name,
	key,
	input_type: inputTypes.ENTER.key,
	data_type: dataType,
	editable: true,
	constraints: { NOTNULL: required, DISPLAY: true },
});

const selectColumn = (name, key, list, { required = false } = {}) => ({
	name,
	key,
	input_type: inputTypes.SELECT.key,
	data_type: datatypes.STRING.key,
	editable: true,
	constraints: { NOTNULL: required, DISPLAY: true },
	[selectOptionsKeyName]: {
		type: arrType,
		list,
	},
});

export const poHeaderColumnsObj = {
	owner_site_code: bqSearchColumn("Owner Site", "owner_site_code", { required: true }),
	vendor_code: bqSearchColumn("Vendor", "vendor_code", { required: true }),
	currency_code: bqSearchColumn("Currency", "currency_code", { required: true }),
	order_date: { ...enterColumn("Order Date", "order_date", datatypes.DATE.key, { required: true }), disabled: true },
	valid_from_date: enterColumn("Valid From", "valid_from_date", datatypes.DATE.key, { required: true }),
	valid_to_date: enterColumn("Valid To", "valid_to_date", datatypes.DATE.key, { required: true }),
	entry_mode: { ...selectColumn("Entry Mode", "entry_mode", entryModeOptions, { required: true }), disabled: true },
	trade_group_name: {
		...enterColumn("Trade Group", "trade_group_name", datatypes.STRING.key, { required: true }),
		disabled: true,
	},
	purchase_term_code: {
		...enterColumn("Term", "purchase_term_code", datatypes.NUMBER.key),
		disabled: true,
	},
	doc_code: selectColumn("Doc Scheme", "doc_code", [{ name: "140", key: 140 }], { required: true }),
	agent_code: bqSearchColumn("Agent", "agent_code"),
	transporter_code: bqSearchColumn("Transporter", "transporter_code"),
	merchandiser_code: bqSearchColumn("Merchandiser", "merchandiser_code"),
	exchange_rate: enterColumn("Exchange Rate", "exchange_rate", datatypes.NUMBER.key),
	channel: bqSearchColumn("Channel", "channel", { required: true }),
	delivery_date: enterColumn("Delivery Date", "delivery_date", datatypes.DATE.key, { required: true }),
	po_no: { ...enterColumn("PO No", "po_no"), disabled: true },
	ginesys_po_id: { ...enterColumn("Ginesys PO Id", "ginesys_po_id", datatypes.NUMBER.key), disabled: true },
	ginesys_scheme_doc_no: { ...enterColumn("Ginesys Scheme Doc No", "ginesys_scheme_doc_no"), disabled: true },
	remarks: enterColumn("Remarks", "remarks", datatypes.LONGTEXT.key),
	is_consignment: { ...selectColumn("Purchase Type", "is_consignment", purchaseTypeOptions), disabled: true },
	is_authorized: selectColumn("Authorized", "is_authorized", boolOptions),
};

/**
 * General header: 4 fields per row on large screens (left → right, top → bottom).
 * Order stays close to Ginesys (site/scheme/date/vendor, dates/currency…, parties, terms).
 */
export const poHeaderEditableGridColumnList = [
	poHeaderColumnsObj.owner_site_code,
	poHeaderColumnsObj.doc_code,
	poHeaderColumnsObj.order_date,
	poHeaderColumnsObj.vendor_code,
	poHeaderColumnsObj.valid_from_date,
	poHeaderColumnsObj.valid_to_date,
	poHeaderColumnsObj.currency_code,
	poHeaderColumnsObj.exchange_rate,
	poHeaderColumnsObj.is_consignment,
	poHeaderColumnsObj.agent_code,
	poHeaderColumnsObj.transporter_code,
	poHeaderColumnsObj.merchandiser_code,
	poHeaderColumnsObj.trade_group_name,
	poHeaderColumnsObj.purchase_term_code,
	poHeaderColumnsObj.entry_mode,
];

/** Own row under the 4-column general grid. */
export const poHeaderChannelDeliveryColumnList = [
	poHeaderColumnsObj.channel,
	poHeaderColumnsObj.delivery_date,
];

/** Selected/view display order (includes system fields after editable block). */
export const poHeaderColumnList = [
	...poHeaderEditableGridColumnList,
	poHeaderColumnsObj.po_no,
	poHeaderColumnsObj.ginesys_scheme_doc_no,
	poHeaderColumnsObj.ginesys_po_id,
	poHeaderColumnsObj.remarks,
	poHeaderColumnsObj.is_authorized,
];

/** Create / edit modal — grid fields + remarks footer */
export const poHeaderEditableColumnList = [
	...poHeaderEditableGridColumnList,
	poHeaderColumnsObj.remarks,
];

/** @deprecated use poHeaderEditableGridColumnList — kept for older imports */
export const poHeaderEditableLeftColumnList = poHeaderEditableGridColumnList;
export const poHeaderEditableRightColumnList = [];
export const poHeaderLeftColumnList = poHeaderEditableGridColumnList;
export const poHeaderRightColumnList = [
	poHeaderColumnsObj.po_no,
	poHeaderColumnsObj.ginesys_scheme_doc_no,
	poHeaderColumnsObj.ginesys_po_id,
	poHeaderColumnsObj.is_authorized,
];

/** Doc Scheme must be a real scheme — not blank / 0 / 1 (manual numbering). */
export const assertValidPoDocCode = (docCode) => {
	const n = docCode == null || docCode === "" ? null : Number(typeof docCode === "object" ? docCode.key ?? docCode.code : docCode);
	if (n == null || !Number.isFinite(n) || n === 0 || n === 1) {
		throw new Error("Doc Scheme is required and cannot be empty, 0, or 1");
	}
};

const requiredFieldText = (value) => {
	if (value == null || value === "") return "";
	if (typeof value === "object") {
		const key = value.key ?? value.code ?? value.barcode ?? "";
		return key == null ? "" : String(key).trim();
	}
	return String(value).trim();
};

/** Stop create/save before the API when a starred header field is empty. */
export const assertRequiredPoHeader = (form) => {
	const columns = [...poHeaderEditableGridColumnList, ...poHeaderChannelDeliveryColumnList];
	const missing = columns.filter((col) => col?.constraints?.NOTNULL && !requiredFieldText(form?.[col.key]));
	if (missing.length) {
		throw new Error(`Fill required fields: ${missing.map((col) => col.name).join(", ")}`);
	}
	assertValidPoDocCode(form?.doc_code);
	assertPoDates(form);
};

/** Calendar date in India, where purchase orders are raised. */
export const todayPoDate = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());

const dateOnly = (value) => String(value || "").slice(0, 10);

export const nextPoDate = (value) => {
	const day = dateOnly(value);
	if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return "";
	const [year, month, date] = day.split("-").map(Number);
	const next = new Date(Date.UTC(year, month - 1, date));
	next.setUTCDate(next.getUTCDate() + 1);
	return next.toISOString().slice(0, 10);
};

/** Indian financial year ends 31 March. */
export const currentFinancialYearEnd = (onDate = todayPoDate()) => {
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOnly(onDate));
	if (!match) return "";
	const year = Number(match[1]);
	const month = Number(match[2]);
	const endYear = month >= 4 ? year + 1 : year;
	return `${endYear}-03-31`;
};

/** Valid From is after Order Date. Valid To defaults to 31 March and must be after Valid From. */
export const assertPoDates = (form) => {
	const order = dateOnly(form?.order_date);
	const from = dateOnly(form?.valid_from_date);
	const to = dateOnly(form?.valid_to_date);
	if (order && from && from <= order) {
		throw new Error("Valid From must be after Order Date");
	}
	if (from && to && to <= from) {
		throw new Error("Valid To must be after Valid From");
	}
};

const toMasterCode = (value) => {
	if (value == null || value === "") return null;
	const n = Number(value);
	return Number.isFinite(n) ? n : value;
};

const isConsignmentFlag = (value) => {
	const text = String(value ?? "").trim().toUpperCase();
	return text === "Y" || text === "1" || text === "TRUE";
};

/**
 * When vendor is selected as an option object, flatten vendor_code and
 * fill trade group, term, purchase type, currency, and exchange rate from FINSL.
 * Changing the owner site clears the vendor first.
 */
export const syncPoHeaderDraft = (prev, next) => {
	const draft = { ...next };
	const prevSite = poPartyCode(prev?.owner_site_code);
	const nextSite = poPartyCode(draft.owner_site_code);
	if (String(prevSite ?? "") !== String(nextSite ?? "")) {
		draft.vendor_code = null;
	}
	return syncPoVendorDefaults(draft);
};

export const syncPoVendorDefaults = (form) => {
	const next = { ...form };
	const vendor = form?.vendor_code;
	if (vendor && typeof vendor === "object") {
		next.vendor_code = vendor.key ?? vendor.code ?? null;
		next.purchase_term_code = toMasterCode(
			vendor.purchase_term_code ?? vendor.PURTERMMAIN_CODE ?? vendor.purchaseTermCode
		);
		next.trade_group_code = toMasterCode(
			vendor.trade_group_code ?? vendor.FINTRADEGRP_CODE ?? vendor.tradeGroupCode
		);
		next.trade_group_name = scalarText(vendor.trade_group_name ?? vendor.tradeGroupName) || "";
		next.currency_code = toMasterCode(
			vendor.currency_code ?? vendor.ADMCURRENCY_CODE ?? vendor.currencyCode
		);
		next.is_consignment = isConsignmentFlag(vendor.consignment ?? vendor.whether_consignment);
		const rate = parseItemRate(vendor.exchange_rate);
		const baseCurrency = vendor.currency_is_base === 1 || vendor.currency_is_base === true || vendor.currency_is_base === "1";
		next.exchange_rate =
			rate != null && rate > 0 ? rate : baseCurrency || next.currency_code === 1 ? 1 : null;
	} else if (vendor == null || vendor === "") {
		next.purchase_term_code = null;
		next.trade_group_code = null;
		next.trade_group_name = "";
		next.currency_code = null;
		next.exchange_rate = null;
		next.is_consignment = false;
	}
	if (!next.valid_to_date) next.valid_to_date = currentFinancialYearEnd();
	return next;
};

export const poItemColumnsObj = {
	barcode: bqSearchColumn("Barcode", "barcode", { required: true }),
	item_description: {
		...enterColumn("Item Description", "item_description", datatypes.STRING.key),
		disabled: true,
	},
	item_code: { ...enterColumn("Item Code", "item_code", datatypes.STRING.key, { required: true }), disabled: true },
	qty: enterColumn("Qty", "qty", datatypes.NUMBER.key, { required: true }),
	rate: { ...enterColumn("Rate", "rate", datatypes.NUMBER.key), disabled: true },
	due_date: enterColumn("Due Date", "due_date", datatypes.DATE.key),
	remarks: enterColumn("Remarks", "remarks"),
};

export const poItemColumnList = [
	poItemColumnsObj.barcode,
	poItemColumnsObj.item_description,
	poItemColumnsObj.item_code,
	poItemColumnsObj.qty,
	poItemColumnsObj.rate,
	poItemColumnsObj.due_date,
	poItemColumnsObj.remarks,
];

export const emptyPoForm = () => ({
	owner_site_code: null,
	doc_code: 140,
	order_date: todayPoDate(),
	po_no: "",
	ginesys_po_id: null,
	ginesys_scheme_doc_no: "",
	vendor_code: null,
	is_consignment: false,
	valid_from_date: null,
	valid_to_date: currentFinancialYearEnd(),
	delivery_date: null,
	channel: null,
	currency_code: null,
	exchange_rate: null,
	agent_code: null,
	agent_rate: null,
	transporter_code: null,
	merchandiser_code: null,
	trade_group_code: null,
	trade_group_name: "",
	purchase_term_code: null,
	remarks: "",
	entry_mode: "I",
	is_authorized: false,
	items: [],
	set_items: [],
	document_charges: [],
	line_charges: [],
	receive_schedules: [],
});

export const emptyPoItemRow = () => ({
	barcode: "",
	item_description: "",
	item_code: "",
	qty: 1,
	rate: null,
	due_date: null,
	remarks: "",
	is_deleted: false,
});
